import { hasEverflowCampaignParameters } from "@portal/app/_common/lib/everflowParameters";
import { EVERFLOW_OFFER_ID } from "@portal/app/_common/types/everflow";
import { getSafeUrlParam } from "@portal/app/_common/lib/urlParamUtils";
import { buildEverflowClickOptions, type EverflowClickOptions } from "./everflowClickOptions";

const TRANSACTION_ID_PARAMETERS = ["_ef_transaction_id", "ef_transaction_id"];

type LandingDecision =
  | { mode: "existing"; transactionId: string }
  | { mode: "legacy"; transactionId: string }
  | { mode: "returning"; transactionId: string }
  | { mode: "create" }
  | { mode: "invalid" };

export type EverflowLanding = { url: string; recoveredMalformedId?: true } & (
  | Exclude<LandingDecision, { mode: "create" }>
  | { mode: "create"; options: EverflowClickOptions }
);

export function isEverflowTransactionId(value: string | undefined): value is string {
  return !!value && /^[a-f0-9]{32}$/i.test(value);
}

/** A broken TID must not silently turn a paid landing into an Organic click. */
function hasRecoverablePartnerContext(search: string): boolean {
  const params = new URLSearchParams(search);
  const affiliateId = getSafeUrlParam("affid", search);
  return (
    params.getAll("affid").length === 1 &&
    !!affiliateId &&
    /^[1-9]\d*$/.test(affiliateId) &&
    Number.isSafeInteger(Number(affiliateId)) &&
    params.getAll("oid").length <= 1 &&
    (!params.has("oid") || getSafeUrlParam("oid", search) === String(EVERFLOW_OFFER_ID))
  );
}

/** Select attribution from captured inputs without reading or changing the browser. */
export function resolveEverflowLanding(
  search: string,
  navigationType: string | undefined,
  cookieTransactionId?: string,
  legacyTransactionId?: string
): LandingDecision {
  const incomingIds = TRANSACTION_ID_PARAMETERS.map((key) => getSafeUrlParam(key, search));
  const transactionId = incomingIds.find(isEverflowTransactionId);
  if (transactionId) {
    if (cookieTransactionId?.toLowerCase() === transactionId.toLowerCase()) {
      return { mode: "returning", transactionId: cookieTransactionId };
    }
    if (legacyTransactionId?.toLowerCase() === transactionId.toLowerCase()) {
      return { mode: "legacy", transactionId: legacyTransactionId };
    }
    return { mode: "existing", transactionId };
  }
  if (incomingIds.some(Boolean) && !hasRecoverablePartnerContext(search))
    return { mode: "invalid" };

  // Reload/history URLs retain campaign parameters whose click was already captured.
  const isRevisit = navigationType === "reload" || navigationType === "back_forward";
  if (isRevisit || !hasEverflowCampaignParameters(search)) {
    if (isEverflowTransactionId(cookieTransactionId)) {
      return { mode: "returning", transactionId: cookieTransactionId };
    }
    if (isEverflowTransactionId(legacyTransactionId)) {
      return { mode: "legacy", transactionId: legacyTransactionId };
    }
  }
  return { mode: "create" };
}

/** Freeze attribution before idle initialization or a navigation drops the query string. */
export function captureEverflowLanding(
  cookieTransactionId?: string,
  legacyTransactionId?: string
): EverflowLanding {
  const url = new URL(window.location.href);
  const navigation = performance.getEntriesByType?.("navigation")[0] as
    | PerformanceNavigationTiming
    | undefined;
  const decision = resolveEverflowLanding(
    url.search,
    navigation?.type,
    cookieTransactionId,
    legacyTransactionId
  );
  const incomingIds = TRANSACTION_ID_PARAMETERS.map((key) => getSafeUrlParam(key, url.search));
  const recovering =
    decision.mode !== "invalid" &&
    incomingIds.some(Boolean) &&
    !incomingIds.some(isEverflowTransactionId);
  const recovery = recovering ? { recoveredMalformedId: true as const } : {};
  const optionsUrl = new URL(url);
  if (recovering) {
    for (const key of TRANSACTION_ID_PARAMETERS) optionsUrl.searchParams.delete(key);
  }
  return decision.mode === "create"
    ? {
        ...decision,
        ...recovery,
        url: url.href,
        options: buildEverflowClickOptions(optionsUrl, document.referrer),
      }
    : { ...decision, ...recovery, url: url.href };
}
